import { useAtom } from "jotai";
import { atomFamily, atomWithStorage } from "jotai/utils";
import { useCallback } from "react";

import { useCurrentAccount } from "@/providers/account/AccountProvider";
import { createStorageKey } from "@/utils/storage";

export type StoredDownload = {
  uuid: string;
  /**
   * ISO timestamp at which the download expires server-side (pinned at
   * creation). Persisted so we can avoid showing/querying downloads that have
   * already expired without hitting the API.
   */
  expiresAt: string;
};

/**
 * The list of async downloads the user has triggered, persisted in
 * localStorage so they survive page reloads and are shared across tabs.
 * Keyed per account so downloads triggered in one workspace aren't shown in
 * another.
 */
const storedDownloadsAtomFamily = atomFamily((accountId: string) =>
  atomWithStorage<StoredDownload[]>(
    createStorageKey("downloads", accountId),
    [],
  ),
);

export function useStoredDownloads() {
  const account = useCurrentAccount();

  const [storedDownloads, setStoredDownloads] = useAtom(
    storedDownloadsAtomFamily(account.id),
  );

  const addDownload = useCallback(
    (uuid: string, expiresAt: string) => {
      setStoredDownloads((prev) => {
        if (prev.some((d) => d.uuid === uuid)) return prev;
        return [{ uuid, expiresAt }, ...prev];
      });
    },
    [setStoredDownloads],
  );

  const removeDownload = useCallback(
    (uuid: string) => {
      setStoredDownloads((prev) => prev.filter((d) => d.uuid !== uuid));
    },
    [setStoredDownloads],
  );

  const removeDownloads = useCallback(
    (uuids: string[]) => {
      if (uuids.length === 0) return;
      const toRemove = new Set(uuids);
      setStoredDownloads((prev) => prev.filter((d) => !toRemove.has(d.uuid)));
    },
    [setStoredDownloads],
  );

  return {
    storedDownloads,
    addDownload,
    removeDownload,
    removeDownloads,
  };
}
